import { useState, type CSSProperties } from "react";
import { NotchPanel, NotchSegmented, NotchedInput, KeycapButton } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

const Lock = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
const Users = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const Globe = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18" />
  </svg>
);
const Link = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M9 17H7A5 5 0 0 1 7 7h2M15 7h2a5 5 0 0 1 0 10h-2M8 12h8" />
  </svg>
);

/**
 * Capstone: the four notch primitives composed into a realistic UI — a
 * "Create Studio" form. NotchPanel (container) + NotchSegmented (visibility) +
 * NotchedInput (fields) + KeycapButton (actions), all in the hex-notch language.
 * Verified light + dark. The --primary on the panel is the redo's indigo accent
 * (a token, not hardcoded) so the whole composition recolors from one value.
 */
export function NotchShowcaseDemo() {
  const [vis, setVis] = useState("workspace");

  return (
    <Section
      id="notch-showcase"
      title="Notch language — composed"
      description="The four primitives as one coherent UI: a Create-Studio form. NotchPanel holds a NotchSegmented (visibility), two NotchedInputs, and a Cancel/Create KeycapButton pair — every surface, control, and edge on the same hex notch. Token-driven: the indigo accent is one --primary value on the panel."
    >
      <VariantBlock
        label="Create Studio"
        caption="panel + segmented + inputs + keycaps — the notch language end to end"
      >
        <NotchPanel
          notch="l"
          elev="panel"
          className="w-full max-w-md"
          style={{ "--primary": "238 79% 64%" } as CSSProperties}
        >
          <h3 className="font-display text-lg font-semibold text-foreground">Create a Studio</h3>
          <p className="mt-1 mb-4 text-sm text-muted-foreground">
            A chat-first space with its own memory, experts, and reports.
          </p>

          <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Name
          </label>
          <NotchedInput notch="b" placeholder="Growth Intelligence" wrapperClassName="mb-4" />

          <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Visibility
          </label>
          <NotchSegmented
            aria-label="Visibility"
            value={vis}
            onValueChange={setVis}
            size="sm"
            notch="b"
            className="mb-5"
            options={[
              { value: "private", label: "Private", icon: <Lock /> },
              { value: "workspace", label: "Workspace", icon: <Users /> },
              { value: "public", label: "Public", icon: <Globe /> },
            ]}
          />

          <div className="flex items-center justify-end gap-2">
            <KeycapButton notch="b" variant="secondary" size="sm">
              Cancel
            </KeycapButton>
            <KeycapButton notch="b" variant="primary" size="sm">
              <Link />
              Create
            </KeycapButton>
          </div>
        </NotchPanel>
      </VariantBlock>
    </Section>
  );
}
